import { DateTime } from 'luxon'

import { createListBundle, isAbortError, parseApiErrors } from '@common/utils'
import propertyUrls from '@rest/pages/Properties/urls'
import unitUrls from '@rest/pages/Units/urls'

const entityName = 'event'

const getRouteParams = (store) => {
  const routeInfo = store.selectRouteInfo()
  const isPropertyLevel = routeInfo?.pattern.includes('properties')
  const entityType = isPropertyLevel ? 'property' : 'unit'
  const entityId = routeInfo?.params?.id
  return { entityType, entityId }
}

const eventListBundle = createListBundle({
  entityName,
  fetchHandler: ({ apiFetch, params, store }) => {
    const property = store.selectCurrentProperty()

    let { startDate, endDate } = params

    if (!DateTime.isDateTime(startDate) || !DateTime.isDateTime(endDate)) {
      endDate = DateTime.now().setZone(property.timezone)
      startDate = endDate.minus({ days: 7 })
    }

    const { entityType, entityId } = getRouteParams(store)

    return apiFetch(
      '/events/',
      {
        localCreatedOnDateAfter: startDate.toISODate(),
        localCreatedOnDateBefore: endDate.toISODate(),
        [entityType]: entityId,
        includeInternalEvents: entityType === 'unit',
        eventClass: params.eventClass || 'SMOKE',
        pageSize: params?.pageSize,
        page: params?.page,
        ordering: params?.ordering,
        search: params?.search,
        id: params?.id,
      },
      { cancelationPrefix: entityName },
    )
  },
  urlTest: (_, pattern, hash) =>
    (pattern === propertyUrls.entity || pattern === unitUrls.entity) &&
    hash === 'events',
})

export default {
  ...eventListBundle,
  doFetchPropertyChargeSummary:
    (payload) =>
    async ({ apiFetch, store }) => {
      const propertyId = store.selectRouteParams().id

      const [startDate, endDate] = payload

      if (!DateTime.isDateTime(startDate) || !DateTime.isDateTime(endDate)) {
        return null
      }

      try {
        const response = await apiFetch(
          `/properties/${propertyId}/smoke_charge_summary/`,
          {
            start: startDate.startOf('day').toISO(),
            end: endDate.endOf('day').toISO(),
          },
          { cancelationPrefix: entityName },
        )
        return response
      } catch (err) {
        if (!isAbortError(err)) throw err
        return null
      }
    },
  doEventListExport:
    () =>
    async ({ apiFetch, store }) => {
      const apiParams = store.selectEventListApiParams()
      const { entityType, entityId } = getRouteParams(store)
      const { count = 9999 } = store.selectEventList()
      try {
        const response = await apiFetch(
          '/events/',
          {
            ...apiParams,
            localCreatedOnDateAfter: apiParams.startDate.toISODate(),
            localCreatedOnDateBefore: apiParams.endDate.toISODate(),
            [entityType]: entityId,
            pageSize: count,
          },
          { cancelationPrefix: entityName },
        )
        if (response.error) throw response.error
        return response.results
      } catch (error) {
        const parsedError = parseApiErrors(error?.response)
        store.doShowSnackbar(parsedError || 'Unable to export events', 'error')
      }
      return []
    },
}
